import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, distance, type Vector2 } from '../match/Vector2';

export interface PassingLaneAssessment {
  target: Vector2;
  distance: number;
  forwardGain: number;
  blockers: number;
  nearestOpponentDistance: number;
  openness: number;
  risk: number;
}

export interface PlayerPerception {
  pressure: number;
  nearestOpponentDistance: number;
  localSpace: number;
  distanceToGoal: number;
  goalAngleQuality: number;
}

export interface PerceptionSnapshot {
  teammates: PlayerRuntimeState[];
  opponents: PlayerRuntimeState[];
  actor: PlayerPerception;
}

/**
 * Single spatial vocabulary for decision making. It deliberately returns
 * immutable values instead of retaining mutable match state between ticks.
 */
export class Perception {
  observe(state: MatchState, actor: PlayerRuntimeState): PerceptionSnapshot {
    const teammates = state.players.filter((player) => player.teamSide === actor.teamSide);
    const opponents = state.players.filter((player) => player.teamSide !== actor.teamSide);
    const opponentDistances = opponents.map((opponent) => distance(opponent.position, actor.position));
    const nearestOpponentDistance = opponentDistances.length ? Math.min(...opponentDistances) : 100;
    const pressure = clamp(opponentDistances.reduce((total, opponentDistance) => (
      opponentDistance <= 8 ? total + (8 - opponentDistance) / 8 : total
    ), 0), 0, 1);
    const distanceToGoal = 100 - PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x);

    return {
      teammates,
      opponents,
      actor: {
        pressure,
        nearestOpponentDistance,
        localSpace: clamp(nearestOpponentDistance / 12, 0, 1),
        distanceToGoal,
        goalAngleQuality: this.goalAngleQuality(actor.teamSide, actor.position),
      },
    };
  }

  assessPassingLane(
    actor: PlayerRuntimeState,
    target: Vector2,
    opponents: readonly PlayerRuntimeState[],
  ): PassingLaneAssessment {
    const start = actor.position;
    const dx = target.x - start.x;
    const dy = target.y - start.y;
    const squaredLength = dx * dx + dy * dy || 1;
    let blockers = 0;
    let nearestOpponentDistance = 100;

    for (const opponent of opponents) {
      const projection = clamp(
        ((opponent.position.x - start.x) * dx + (opponent.position.y - start.y) * dy) / squaredLength,
        0,
        1,
      );
      const closest = { x: start.x + dx * projection, y: start.y + dy * projection };
      const laneDistance = distance(opponent.position, closest);
      nearestOpponentDistance = Math.min(nearestOpponentDistance, laneDistance);
      if (laneDistance < 4.5) blockers++;
    }

    const passDistance = distance(start, target);
    const direction = actor.teamSide === 0 ? 1 : -1;
    const risk = clamp(blockers * 0.22 + passDistance / 180, 0, 1);
    return {
      target: { ...target },
      distance: passDistance,
      forwardGain: (target.x - start.x) * direction,
      blockers,
      nearestOpponentDistance,
      openness: 1 - risk,
      risk,
    };
  }

  private goalAngleQuality(side: 0 | 1, position: Vector2): number {
    const longitudinal = Math.max(0.001, Math.abs(PITCH_GEOMETRY.attackingGoalX(side) - position.x));
    const lateral = Math.abs(PITCH_GEOMETRY.centre.y - position.y);
    const angle = Math.atan2(lateral, longitudinal);
    return clamp(1 - angle / (Math.PI / 2), 0, 1);
  }
}
